import { useMemo } from "react";
// routes
import { paths } from "src/routes/paths";
// locales
import { useLocales } from "src/locales";

// auth
import { useAuthContext } from "src/auth/hooks";

// components
import Iconify from "src/components/iconify";

//
import { ENTERPRISE_ROLES } from "src/core/lib/constants";

// ----------------------------------------------------------------------

const icon = name => (
  // <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  <Iconify icon={name} />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  provider: icon("ic:baseline-supervisor-account"),
  asset: icon("ic:baseline-api"),
  supplies: icon("tabler:package"),
  task: icon("ic:twotone-inventory"),
  chevronRight: icon("ic:baseline-chevron-right"),
  audit: icon("ic:twotone-query-stats"),
  workRequest: icon("carbon:request-quote"),
  parameters: icon("gg:list"),
  plannedTask: icon("et:calendar"),
  user: icon("lucide:user-cog-2"),
  currency: icon("ic:outline-monetization-on"),
  structure: icon("ph:tree-structure-light"),
  category: icon("tabler:category-plus"),
  dashboard: icon("icon-park-outline:chart-line"),
  reportsDashboard: icon("ic:outline-dashboard"),
  workOrder: icon("grommet-icons:host-maintenance"),
  meter: icon("ep:odometer"),
  inventory: icon("ic:outline-receipt-long"),
  enterprise: icon("solar:buildings-2-bold"),
  component: icon("fa6-solid:gears"),
  program: icon("mdi:calendar-text"),
  equipmentStop: icon("carbon:stop-filled"),
  globalConfiguration: icon("famicons:globe-outline"),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user, hasPermission, hasEnterpriseRole } = useAuthContext();

  const data = useMemo(() => {
    const filterItems = items =>
      items
        .filter(item => {
          // El owner tiene todos los permisos: ningún permiso distingue lo que es solo suyo.
          if (item.ownerOnly && user?.owner !== true) return false;
          if (item.roles && item.roles.length > 0) {
            if (!item.roles.find(x => hasEnterpriseRole(x))) return false;
          }
          if (!item.requiredPermissions) return true;
          return item.requiredPermissions.find(x => hasPermission(x));
        })
        .map(item => ({
          ...item,
          children: item.children ? filterItems(item.children) : undefined,
        }));

    const sections = [
      // DASHBOARD
      // ----------------------------------------------------------------------
      {
        subheader: t("dashboard"),
        items: filterItems([
          {
            title: t("dashboard"),
            path: paths.dashboard.dashboard,
            icon: ICONS.dashboard,
            requiredPermissions: ["readDashboard"],
          },
        ]),
      },
      // ENTITIES
      // ----------------------------------------------------------------------
      {
        subheader: t("entities"),
        items: filterItems([
          {
            title: t("assets"),
            path: paths.dashboard.asset.root,
            icon: ICONS.asset,
            hasChildRoutes: true,
            requiredPermissions: ["readAsset"],
          },
          {
            title: t("components"),
            path: paths.dashboard.component.root,
            icon: ICONS.component,
            hasChildRoutes: true,
            requiredPermissions: ["readComponent"],
          },
          {
            title: t("structures"),
            path: paths.dashboard.structure.root,
            icon: ICONS.structure,
            hasChildRoutes: true,
            requiredPermissions: ["readStructure"],
          },
          {
            title: t("label:tasks"),
            path: paths.dashboard.task.root,
            icon: ICONS.task,
            requiredPermissions: ["readTask", "readTaskGroup"],
            children: [
              {
                title: t("label:tasks"),
                path: paths.dashboard.task.grid,
                requiredPermissions: ["readTask"],
              },
              {
                title: t("taskGroups", { label: t("label:tasks") }),
                path: paths.dashboard.taskGroup.grid,
                requiredPermissions: ["readTaskGroup"],
              },
            ],
          },
          {
            title: t("supplies"),
            path: paths.dashboard.supply.root,
            icon: ICONS.supplies,
            hasChildRoutes: true,
            requiredPermissions: ["readSupply"],
          },
          {
            title: t("providers"),
            path: paths.dashboard.provider.root,
            icon: ICONS.provider,
            hasChildRoutes: true,
            requiredPermissions: ["readProvider"],
          },
        ]),
      },
      // OPERATIVE
      // ----------------------------------------------------------------------
      {
        subheader: t("operability"),
        items: filterItems([
          {
            title: t("workRequests"),
            path: paths.dashboard.workRequest.grid,
            requiredPermissions: ["readWorkRequest"],
            icon: ICONS.workRequest,
            hasChildRoutes: true,
          },
          {
            title: t("programs"),
            path: paths.dashboard.program.grid,
            icon: ICONS.program,
            requiredPermissions: ["readProgram"],
            hasChildRoutes: true,
          },
          {
            title: t("measurements"),
            path: paths.dashboard.meter.root,
            icon: ICONS.meter,
            requiredPermissions: ["readReading", "readMeter"],
            children: [
              {
                title: t("meters"),
                path: paths.dashboard.meter.grid,
                requiredPermissions: ["readMeter"],
              },
              {
                title: t("readings"),
                path: paths.dashboard.reading.grid,
                requiredPermissions: ["readReading"],
              },
            ],
          },
          {
            title: t("plannedTasks", { tasks: t("label:tasks") }),
            path: paths.dashboard.plannedTask.grid,
            icon: ICONS.plannedTask,
            requiredPermissions: ["readPlannedTask"],
            hasChildRoutes: true,
          },
          {
            title: t("workOrders"),
            path: paths.dashboard.workOrder.grid,
            icon: ICONS.workOrder,
            requiredPermissions: ["readWorkOrder"],
            hasChildRoutes: true,
          },
          {
            title: t("equipmentStops", { label: t("equipment") }),
            path: paths.dashboard.equipmentStop.root,
            icon: ICONS.equipmentStop,
            requiredPermissions: ["readEquipmentStop", "readScheduledOperation"],
            children: [
              {
                title: t("equipmentStops", { label: t("equipment") }),
                path: paths.dashboard.equipmentStop.grid,
                requiredPermissions: ["readEquipmentStop"],
              },
              {
                title: t("scheduledOperations"),
                path: paths.dashboard.scheduledOperation.grid,
                requiredPermissions: ["readScheduledOperation"],
              },
            ],
          },
          {
            title: t("inventory"),
            path: paths.dashboard.inventory.root,
            icon: ICONS.inventory,
            requiredPermissions: ["readVoucher", "readVoucherType"],
            children: [
              {
                title: t("vouchers"),
                path: paths.dashboard.voucher.grid,
                requiredPermissions: ["readVoucher"],
              },
              {
                title: t("voucherTypes"),
                path: paths.dashboard.voucherType.grid,
                requiredPermissions: ["readVoucherType"],
              },
            ],
          },
        ]),
      },
      // REPORT
      // ----------------------------------------------------------------------
      {
        subheader: t("reportsDashboard"),
        items: filterItems([
          {
            title: t("reportsDashboard"),
            path: paths.dashboard.reportsDashboard.root,
            icon: ICONS.reportsDashboard,
            requiredPermissions: ["readReports"],
            hasChildRoutes: true,
          },
        ]),
      },

      // CONFIGURATIONS
      // ----------------------------------------------------------------------
      {
        subheader: t("configurations"),
        items: filterItems([
          // SYSTEM
          {
            title: t("globalConfiguration"),
            path: paths.dashboard.system.root,
            requiredPermissions: ["updateSystemConfiguration", "updateLabels"],
            icon: ICONS.globalConfiguration,
            children: [
              {
                title: t("configuration"),
                path: paths.dashboard.system.globalConfiguration,
                requiredPermissions: ["updateSystemConfiguration"],
              },
              {
                title: t("labels"),
                path: paths.dashboard.system.labels,
                requiredPermissions: ["updateLabels"],
              },
              {
                title: t("mcpConnections"),
                path: paths.dashboard.system.mcpConnections,
                ownerOnly: true,
              },
            ],
          },
          // USER
          {
            title: t("users"),
            path: paths.dashboard.user.root,
            icon: ICONS.user,
            requiredPermissions: ["readUser", "readRole", "readWorkTeam"],
            children: [
              {
                title: t("users"),
                path: paths.dashboard.user.grid,
                requiredPermissions: ["readUser"],
              },
              {
                title: t("userRoles"),
                path: paths.dashboard.role.grid,
                requiredPermissions: ["readRole"],
              },
              {
                title: t("workTeams"),
                path: paths.dashboard.workTeam.root,
                requiredPermissions: ["readWorkTeam"],
              },
            ],
          },
          // CURRENCY
          {
            title: t("currencyTypes"),
            path: paths.dashboard.currency.root,
            requiredPermissions: ["readCurrency", "readExchangeRate"],
            children: [
              { title: t("currencies"), path: paths.dashboard.currency.grid },
              { title: t("exchangeRates"), path: paths.dashboard.exchangeRate.grid },
            ],
            icon: ICONS.currency,
          },
          // CATEGORY
          {
            title: t("requestCategories"),
            requiredPermissions: ["readCategory"],
            path: paths.dashboard.category.grid,
            icon: ICONS.category,
          },
          // PARAMETERS
          {
            title: t("parameters"),
            requiredPermissions: ["readParameter", "readParameterType"],
            path: paths.dashboard.parameter.root,
            icon: ICONS.parameters,
            children: [
              {
                title: t("parameters"),
                path: paths.dashboard.parameter.grid,
                requiredPermissions: ["readParameter"],
              },
              {
                title: t("parameterTypes"),
                path: paths.dashboard.parameterType.grid,
                requiredPermissions: ["readParameterType"],
              },
            ],
          },
          // AUDIT
          {
            title: t("audit"),
            path: paths.dashboard.audit.root,
            requiredPermissions: ["readAudit"],
            icon: ICONS.audit,
          },
          // MANAGEMENT
          {
            title: t("management"),
            path: paths.management.enterprise.root,
            roles: [ENTERPRISE_ROLES.admin],
            icon: ICONS.enterprise,
            children: [
              {
                title: t("enterprise"),
                path: paths.management.enterprise.grid,
                roles: [ENTERPRISE_ROLES.admin],
              },
              {
                title: t("gptConfiguration"),
                path: paths.management.gpt.edit,
                roles: [ENTERPRISE_ROLES.admin],
              },
            ],
          },
        ]),
      },
    ];

    return sections.filter(section => section.items && section.items.length > 0);
  }, [t, user, hasPermission, hasEnterpriseRole]);

  return data;
}
